import Button from "components/button";
import ColorPicker from "components/colorPicker";
import { CollapsibleSection } from "components/collapsibleSection";
import Dropzone from "components/dropzone";
import { buildTailwindPreviewHtml } from "components/htmlPreviewButton";
import Input from "components/input";
import { Label } from "components/label";
import { SwitchButton } from "components/switchButton";
import Textarea from "components/textarea";
import { HttpError } from "@refinedev/core";
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/outline";
import { IEditOrgForm } from "interfaces";
import { get } from "lodash";
import { useState } from "react";
import { Controller, useFieldArray, UseFormReturn } from "react-hook-form";
import { CONTENT_LANGUAGES } from "utills/constants";

const MAXIMUM_MENU_NUMBER = 3;
const DROPZONE_FIELD_CLASSNAME = "sm:col-span-3 flex flex-col pb-3";
const DROPZONE_ERROR_CLASSNAME = "error-text mt-2 min-h-4";
const FOOTER_HTML_LANGUAGES = [
  { value: "vi", label: "Vietnamese", fieldName: "fipix_footer_html_vi" },
  { value: "en", label: "English", fieldName: "fipix_footer_html_en" },
] as const;

export const FipixWhitelabelSection = (
  props: UseFormReturn<IEditOrgForm, HttpError, IEditOrgForm>
) => {
  const {
    register,
    watch,
    control,
    formState: { errors },
  } = props;
  const [footerHtmlTabs, setFooterHtmlTabs] = useState<
    Record<string, "edit" | "preview">
  >({
    vi: "edit",
    en: "edit",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "fipix_menu",
  });

  return (
    <CollapsibleSection
      label="3rd party integration - Fipix Whitelabel"
      hideArrow={!watch("use_fipix_integration")}
      hideContent={!watch("use_fipix_integration")}
      isAnimated={false}
      customSubLabel={
        <div className="mt-3">
          <Controller
            control={control}
            name="use_fipix_integration"
            defaultValue={false}
            render={({ field: { onChange, value = false } }) => (
              <SwitchButton
                checked={value}
                onChange={onChange}
                label="Use Fipix integration"
                hint="Used to configure the Fipix whitelabel photo flow"
              />
            )}
          />
          {errors["use_fipix_integration"] ? (
            <p className="error-text">
              <span className="font-medium">
                {errors["use_fipix_integration"]?.message}
              </span>
            </p>
          ) : null}
        </div>
      }
    >
      <div className="divide-y divide-gray-100">
        <div className="px-4 py-6 sm:px-6">
          <div className="grid gap-x-4 gap-y-6 sm:grid-cols-6 lg:gap-x-6">
            <div className="sm:col-span-3">
              <Label id="fipix_domain" name="Fipix domain name" required />
              <Input
                id="fipix_domain"
                type="text"
                placeholder="actiup.fipix.net"
                error={errors["fipix_domain"]}
                {...register("fipix_domain")}
              />
              {errors["fipix_domain"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["fipix_domain"].message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className={DROPZONE_FIELD_CLASSNAME}>
              <Label id="fipix_brand_logo_url" name="Brand logo URL" required />
              <Controller
                control={control}
                name="fipix_brand_logo_url"
                defaultValue=""
                render={({ field: { onChange, value = "" } }) => (
                  <Dropzone
                    id="fipix_brand_logo_url"
                    onChange={(imageUrl: string) => onChange(imageUrl)}
                    value={value}
                  />
                )}
              />
              {errors["fipix_brand_logo_url"] ? (
                <p className={DROPZONE_ERROR_CLASSNAME}>
                  <span className="font-medium">
                    {errors["fipix_brand_logo_url"].message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label id="fipix_seo_title" name="SEO title" required />
              <Input
                id="fipix_seo_title"
                type="text"
                placeholder="SEO title"
                error={errors["fipix_seo_title"]}
                {...register("fipix_seo_title")}
              />
              {errors["fipix_seo_title"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["fipix_seo_title"].message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-3">
              <Label
                id="fipix_seo_description"
                name="SEO description"
                required
              />
              <Input
                id="fipix_seo_description"
                type="text"
                placeholder="SEO description"
                error={errors["fipix_seo_description"]}
                {...register("fipix_seo_description")}
              />
              {errors["fipix_seo_description"] ? (
                <p className="error-text">
                  <span className="font-medium">
                    {errors["fipix_seo_description"].message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className={DROPZONE_FIELD_CLASSNAME}>
              <Label id="fipix_seo_og_image" name="SEO OG image" required />
              <Controller
                control={control}
                name="fipix_seo_og_image"
                defaultValue=""
                render={({ field: { onChange, value = "" } }) => (
                  <Dropzone
                    id="fipix_seo_og_image"
                    onChange={(imageUrl: string) => onChange(imageUrl)}
                    value={value}
                  />
                )}
              />
              {errors["fipix_seo_og_image"] ? (
                <p className={DROPZONE_ERROR_CLASSNAME}>
                  <span className="font-medium">
                    {errors["fipix_seo_og_image"].message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className={DROPZONE_FIELD_CLASSNAME}>
              <Label id="fipix_favicon" name="Favicon URL" required />
              <Controller
                control={control}
                name="fipix_favicon"
                defaultValue=""
                render={({ field: { onChange, value = "" } }) => (
                  <Dropzone
                    id="fipix_favicon"
                    customAccept={{
                      "image/file": [".ico"],
                    }}
                    customInvalidTypeMessage={
                      "Only ICO file formats are supported."
                    }
                    onChange={(imageUrl: string) => onChange(imageUrl)}
                    value={value}
                  />
                )}
              />
              {errors["fipix_favicon"] ? (
                <p className={DROPZONE_ERROR_CLASSNAME}>
                  <span className="font-medium">
                    {errors["fipix_favicon"].message}
                  </span>
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-2">
              <Label id="fipix_background_color" name="Background color" />
              <Controller
                control={control}
                name="fipix_background_color"
                render={({ field: { onChange, value = "" } }) => (
                  <ColorPicker
                    value={value}
                    onChange={onChange}
                    placeholder="Background color"
                  />
                )}
              />
            </div>
            <div className="sm:col-span-2">
              <Label
                id="fipix_second_background_color"
                name="Second background color"
              />
              <Controller
                control={control}
                name="fipix_second_background_color"
                render={({ field: { onChange, value = "" } }) => (
                  <ColorPicker
                    value={value}
                    onChange={onChange}
                    placeholder="Second background color"
                  />
                )}
              />
            </div>
            <div className="sm:col-span-2">
              <Label id="fipix_main_text_color" name="Main text color" />
              <Controller
                control={control}
                name="fipix_main_text_color"
                render={({ field: { onChange, value = "" } }) => (
                  <ColorPicker
                    value={value}
                    onChange={onChange}
                    placeholder="Main text color"
                  />
                )}
              />
            </div>
            <div className="sm:col-span-3">
              <Label
                id="fipix_header_background_color"
                name="Header background color"
              />
              <Controller
                control={control}
                name="fipix_header_background_color"
                render={({ field: { onChange, value = "" } }) => (
                  <ColorPicker
                    value={value}
                    onChange={onChange}
                    placeholder="Header background color"
                  />
                )}
              />
            </div>
            <div className="sm:col-span-3">
              <Label id="fipix_menu_text_color" name="Menu text color" />
              <Controller
                control={control}
                name="fipix_menu_text_color"
                render={({ field: { onChange, value = "" } }) => (
                  <ColorPicker
                    value={value}
                    onChange={onChange}
                    placeholder="Menu text color"
                  />
                )}
              />
            </div>
            <div className="col-span-full">
              <Label id="fipix_menu" name="Menu" />
              <div>
                {fields.length < MAXIMUM_MENU_NUMBER ? (
                  <div className="col-span-full">
                    <Button
                      type="button"
                      className="w-fit p-2.5 text-sm bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
                      onClick={() => {
                        append({ url: "", name_en: "", name_vi: "" });
                      }}
                    >
                      <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
                      <span className="shrink-0 text-sm">Add more</span>
                    </Button>
                  </div>
                ) : null}
                <div>
                  {fields.map((field, index: number) => (
                    <div key={field.id} className="mt-4 sm:mt-6">
                      <div className="flex justify-between items-center">
                        <Label id="fipix_menu" name={`Menu ${index + 1}`} />
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="mb-2"
                        >
                          <TrashIcon className="w-5 h-5 text-brand-500" />
                        </button>
                      </div>
                      <div className="grid gap-x-4 gap-y-4 sm:grid-cols-6">
                        {CONTENT_LANGUAGES.map((lang) => (
                          <div key={lang.value} className="sm:col-span-2">
                            <div className="relative">
                              <Input
                                id={`fipix_menu.${index}`}
                                type="text"
                                placeholder="Menu name"
                                className="pr-11"
                                error={get(
                                  errors,
                                  `fipix_menu.${index}.name_${lang.value}`,
                                  ""
                                )}
                                {...register(
                                  `fipix_menu.${index}.name_${lang.value}`
                                )}
                              />
                              <img
                                src={lang.icon}
                                alt="Language Icon"
                                className="absolute top-1/2 right-3 -translate-y-1/2"
                              />
                            </div>
                            {get(
                              errors,
                              `fipix_menu.${index}.name_${lang.value}`,
                              ""
                            ) ? (
                              <p className="error-text">
                                <span className="font-medium">
                                  {get(
                                    errors,
                                    `fipix_menu.${index}.name_${lang.value}.message`,
                                    ""
                                  )}
                                </span>
                              </p>
                            ) : null}
                          </div>
                        ))}
                        <div className="sm:col-span-2">
                          <Input
                            id={`fipix_menu.${index}.url`}
                            type="text"
                            placeholder="URL"
                            error={get(errors, `fipix_menu.${index}.url`, "")}
                            {...register(`fipix_menu.${index}.url`)}
                          />
                          {get(errors, `fipix_menu.${index}.url`, "") ? (
                            <p className="error-text">
                              <span className="font-medium">
                                {get(
                                  errors,
                                  `fipix_menu.${index}.url.message`,
                                  ""
                                )}
                              </span>
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-span-full flex flex-col gap-y-6">
              {FOOTER_HTML_LANGUAGES.map((lang) => {
                const fieldPath = lang.fieldName;
                const activeTab = footerHtmlTabs[lang.value] ?? "edit";
                const fieldError = get(errors, fieldPath, "");
                const html = watch(fieldPath);

                return (
                  <div key={lang.value}>
                    <Label
                      id={fieldPath}
                      name={`Footer HTML (${lang.label})`}
                    />
                    <div className="flex flex-col gap-0">
                      <div className="flex border border-b-0 border-gray-200 rounded-t-md overflow-hidden w-fit">
                        <button
                          type="button"
                          onClick={() =>
                            setFooterHtmlTabs((prev) => ({
                              ...prev,
                              [lang.value]: "edit",
                            }))
                          }
                          className={`px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === "edit"
                              ? "bg-white text-gray-900 border-b-2 border-b-blue-500"
                              : "bg-gray-50 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setFooterHtmlTabs((prev) => ({
                              ...prev,
                              [lang.value]: "preview",
                            }))
                          }
                          className={`px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === "preview"
                              ? "bg-white text-gray-900 border-b-2 border-b-blue-500"
                              : "bg-gray-50 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          Preview
                        </button>
                      </div>
                      <div className="border border-gray-200 rounded-b-md rounded-tr-md">
                        {activeTab === "edit" ? (
                          <Textarea
                            id={fieldPath}
                            placeholder={`Footer HTML (${lang.label})`}
                            error={fieldError}
                            className="rounded-none rounded-b-md border-0 min-h-[320px]"
                            {...register(fieldPath)}
                          />
                        ) : (
                          <iframe
                            title={`Footer HTML preview (${lang.label})`}
                            srcDoc={buildTailwindPreviewHtml(html)}
                            sandbox="allow-scripts"
                            className="w-full min-h-[400px] border-0 rounded-b-md rounded-tr-md bg-white"
                          />
                        )}
                      </div>
                    </div>
                    {fieldError ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(errors, `${fieldPath}.message`, "")}
                        </span>
                      </p>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
};
